// @flow
import React from 'react';
import * as Yup from 'yup';
import swal from '@sweetalert/with-react';
import styled from 'styled-components';
import { Formik, Form } from 'formik';
import queryString from 'query-string';
import { clearfix, media } from 'styles/Mixins';
import SignupAction from 'actions/AuthenticationAction';
import HomieContext from 'HomieContext';
import { withRouter } from 'react-router-dom';
import googleTagManager from 'utils/GoogleTagManager';
import { ButtonDefault } from 'styles/Home/StyledHome';
import TermAndConditionsView from 'components/authentication/sign_up/TermAndConditions';
import FormFields from 'components/authentication/sign_up/FormFields';
import FormExternal from 'modules/external_tenant/components/index/FormFieldsSignUp';
import { PrimaryButtonWhite } from 'styles/Buttons';
import parsePhoneNumber from 'libphonenumber-js';
import Cookie from 'utils/Cookie';

const Container = styled.div`
  ${clearfix}
  margin: 0 auto;
  width: ${({ sizeContainer }) => sizeContainer};
  ${media.md`
    padding-top: 0;
    margin-left:auto;
    margin-right:auto;
  `}
`;

const ContainerForm = styled.div`
  ${props => (props.isResponsive ? '30px 0 0 0' : '30px 0')}
  padding: 30px 0;
  text-align: center;
  ${media.md`
    padding: 0;
  `}
`;

const Label = styled.label.attrs({
  style: ({ fsize }) => ({
    fontSize: fsize
  })
})`
  display: ${({ defaultSign }) => (defaultSign ? 'block' : 'none')};
  color: #4a4a4a;
  line-height: 24px;
  font-weight: 500;
  margin-bottom: ${({ modal }) => (modal ? 10 : 20)}px;
`;

const StyledSubmitButton = styled(ButtonDefault)`
  width: 100%;
  line-height: 19px;
  margin: ${props => (props.isResponsive ? '30px 0 0 0' : '30px 0 10px 0')};
  background-color: #278c7e;
  transition: background-color 0.2s linear;
  &:hover {
    background-color: #278c7e;
  }
  &:disabled {
    background: #ececec;
    color: #ffffff;
  }
`;

const StyledSubmitExternal = styled(StyledSubmitButton)`
  margin: 20px 0 10px 0;
`;

const StyledButtonWhite = styled(PrimaryButtonWhite)`
  margin: ${props => (props.isResponsive ? '15px 0 0 0' : '5px 0 0 0')};
`;

const Spacer = styled.div`
  padding-bottom: 20px;
`;

const StyledBoxError = styled.div`
  display: ${props => (props.hasError ? 'block' : 'none')};
  color: #ff1400;
  font-size: 11px;
  height: 34px;
  width: 301px;
  border-radius: 3px;
  background-color: #ffd5d5;
  margin-bottom: 5px;
  padding: 2px;
  text-align: left;
`;

const TermAndConditions = (props: any) => {
  if (props.showTerm) return <TermAndConditionsView {...props} />;

  return null;
};

type FormFormik = {
  name: string,
  last_name: string,
  second_last_name: string,
  email: string,
  lada_plus_number: string,
  password: string
};

type PropsType = {
  sizeContainer: string,
  defaultSign: boolean,
  status: boolean,
  handleRedirect: any => void,
  history: any,
  isResponsive?: boolean,
  showTerms?: boolean,
  isExternalTenant?: boolean,
  onClickSearchHome?: () => void,
  hideButtonSubmitHome?: boolean
};

type StateType = {
  countryCode: number,
  pickingCountryCode: boolean,
  termsAndConditions: boolean,
  leadType: string
};

class View extends React.Component<PropsType, StateType> {
  static defaultProps = {
    showTerms: true,
    isExternalTenant: false
  };

  constructor(props: PropsType) {
    super(props);
    this.state = this.initialState;
  }

  get initialState() {
    let status = !this.props.status;
    return {
      countryCode: 52,
      pickingCountryCode: false,
      termsAndConditions: status,
      externalCountryCode: 52,
      leadType: this.props.status ? 'crm_tenant' : 'crm_owner'
    };
  }

  defaultValues = (): FormFormik => {
    const externalData = this.props.isExternalTenant
      ? {
          full_name: '',
          owner_email: '',
          neighborhood: '',
          price: '',
          external_phone: ''
        }
      : null;

    return {
      name: '',
      last_name: '',
      second_last_name: '',
      lada_plus_number: '',
      phone: '',
      email: '',
      password: '',
      ...externalData
    };
  };

  externalTenantValidation = () => {
    if (this.props.isExternalTenant) {
      return {
        neighborhood: Yup.string().required('Campo requerido'),
        price: Yup.number()
          .typeError('Valor numérico requerido')
          .min(1.0, 'El precio debe ser mayor a 0')
          .required('Campo requerido'),
        external_phone: Yup.string()
          .required('Campo requerido')
          .matches(
            /^[0-9]{10}$/,
            'Ingresa un número de teléfono válido (10 dígitos)'
          )
      };
    }

    return null;
  };

  validationSchema = () => {
    const state = this.state;
    return Yup.object().shape({
      name: Yup.string()
        .nullable()
        .matches(/^[a-zA-Z\s]*$/, 'Ingresa solo letras')
        .required('Campo requerido')
        .trim('Campo requerido'),
      last_name: Yup.string()
        .matches(/^[a-zA-Z\s]*$/, 'Ingresa solo letras')
        .required('Campo requerido')
        .trim('Campo requerido'),
      lada_plus_number: Yup.string()
        .required('Campo requerido')
        .test('validPhone', 'Ingresa un número de teléfono válido', value => {
          const phoneNumber = parsePhoneNumber(`+${state.countryCode}${value}`);
          return phoneNumber && phoneNumber.isPossible();
        }),
      email: Yup.string()
        .email('Ingresa un correo electrónico valido')
        .required('Campo requerido')
        .matches(
          /^[\w-\.+]+@([\w-]+\.)(net|com|org|gob|edu|int|[\w]{2})(.[\w]{2})?$/,
          'Ingresa un correo electrónico válido'
        ),
      password: Yup.string().required('Campo requerido'),
      ...this.externalTenantValidation()
    });
  };

  isExternalType = (): void => {
    if (this.props.isExternalTenant) {
      return {
        tenant_type: 'external'
      };
    }

    return null;
  };

  submitData = (values: FormFormik): void => {
    const queries = queryString.parse(location?.search);
    if (this.state.termsAndConditions) {
      this.context.showLoadingProcessView();
      const { leadType } = this.state;
      const data = {
        user: {
          name: values.name.trim(),
          last_name: values.last_name.trim(),
          second_last_name: '',
          mobile_phone: `+${this.state.countryCode}${values.lada_plus_number}`,
          email: values.email,
          password: values.password,
          password_confirmation: values.password,
          terms_and_conditions: this.state.termsAndConditions,
          is_subscribed: true,
          lead_type: leadType,
          register_url: location?.href,
          campaign: queries?.utm_campaign || Cookie.get('utm_campaign'),
          utm_medium: queries?.utm_medium || Cookie.get('utm_medium'),
          ...this.isExternalType()
        },
        from_axios: true,
        error: false,
        role: 'user',
        marketing_source: 'Página Web',
        source: queries?.utm_source || Cookie.get('utm_source') || 'organic'
      };

      SignupAction.registerUser(
        data,
        false,
        () => {
          if (this.props.status) {
            googleTagManager.methods.triggerFromSignUpOwner(
              window.location.href
            );
          } else {
            googleTagManager.methods.triggerFromOnboarding(
              window.location.href
            );
          }

          this.context.hideLoadingProcessView();

          if (this.props.isExternalTenant) {
            this.props.handleRedirect({
              owner_name: values.full_name || undefined,
              owner_email: values.owner_email || undefined,
              neighborhood: values.neighborhood,
              price: values.price,
              externalPhone: values.external_phone,
              countryCode: this.state.externalCountryCode
            });
          } else {
            this.props.handleRedirect(null);
          }
        },
        (error: any) => {
          this.context.hideLoadingProcessView();
          swal({
            title: '¡Ha ocurrido un error!',
            text: error.responseJSON.message,
            icon: 'error',
            timer: 5000
          });
        }
      );
    } else {
      swal(
        '¡Algo falta!',
        'Debes aceptar los términos y condiciones',
        'warning',
        {
          timer: 5000
        }
      );
    }
  };

  _toggleCountryCodeSelect = (): void => {
    this.setState(prevState => {
      return {
        pickingCountryCode: !prevState.pickingCountryCode
      };
    });
  };

  _handleCountryCodeClick = (): void => {
    this._toggleCountryCodeSelect();
  };

  _handleCountryCodePicked = (element: any): void => {
    this.setState(
      {
        countryCode: +element.value
      },
      this._toggleCountryCodeSelect
    );
  };

  _handleExternalCountryCode = (value): void => {
    this.setState({ externalCountryCode: +value });
  };

  handleInputChange = (value: boolean): void => {
    this.setState({
      ['termsAndConditions']: value
    });
  };

  goToLegal = (section: string = '') => {
    this.props.history.push(`/legal/${section}`);
  };

  goToLegalPrivacy = () => {
    this.goToLegal('aviso-de-privacidad');
  };

  goToLegalTAC = () => {
    this.goToLegal('terminos-y-condiciones-generales-homie');
  };

  isVisibleButtonHome = () => {
    const { isExternalTenant, status, hideButtonSubmitHome } = this.props;
    return !isExternalTenant && !status && !hideButtonSubmitHome;
  };

  objectEntries = (value: any): boolean => Object.entries(value).length;

  SignUpForm = (formik: any) => {
    const {
      isResponsive = false,
      showTerms,
      isExternalTenant,
      onClickSearchHome
    } = this.props;
    const { errors, touched, values, setFieldValue, handleSubmit } = formik;
    const {
      termsAndConditions,
      pickingCountryCode,
      countryCode,
      externalCountryCode
    } = this.state;

    const isEmptyFields = (): boolean => {
      const keys = Object.keys(values);
      const empty = keys.filter(key => values[key] === '').length;
      return empty === this.objectEntries(values);
    };

    const additionalValidation = () => {
      onClickSearchHome();
      if (isEmptyFields()) {
        this.props.handleRedirect(null);
      } else {
        this.setState({
          leadType: 'crm_tenant'
        });
        handleSubmit();
      }
    };

    return (
      <Form>
        {this.isVisibleButtonHome() && (
          <StyledBoxError
            hasError={this.objectEntries(errors) === this.objectEntries(values)}
          >
            *Todos los campos en el registro son necesarios para completar tu
            solicitud
          </StyledBoxError>
        )}
        <FormFields
          showErrorText={
            !(this.objectEntries(errors) === this.objectEntries(values))
          }
          isExternalTenant={isExternalTenant}
          errors={errors}
          touched={touched}
          handleCountryCodeClick={this._handleCountryCodeClick}
          handleCountryCodePicked={this._handleCountryCodePicked}
          pickingCountryCode={pickingCountryCode}
          countryCode={countryCode}
        />

        <FormExternal
          externalTenant={isExternalTenant}
          errors={errors}
          touched={touched}
          handleCountryCodePicked={this._handleExternalCountryCode}
          countryCode={externalCountryCode}
          values={values}
          setFieldValue={setFieldValue}
        />

        <TermAndConditions
          fontSize="12"
          showTerm={showTerms}
          statusTerm={this.props.status}
          termsAndCon={termsAndConditions}
          onInputChange={this.handleInputChange}
          onLegalPrivacy={this.goToLegalPrivacy}
          onLegalTAC={this.goToLegalTAC}
        />
        {isExternalTenant && (
          <StyledSubmitExternal type="submit">
            Continuar con aplicación
          </StyledSubmitExternal>
        )}

        {!isExternalTenant && (
          <StyledSubmitButton isResponsive={isResponsive} type="submit">
            Continuar
          </StyledSubmitButton>
        )}

        {this.isVisibleButtonHome() && (
          <StyledButtonWhite
            type="button"
            isResponsive={isResponsive}
            onClick={() => additionalValidation()}
          >
            Busco un hogar
          </StyledButtonWhite>
        )}
        <Spacer />
      </Form>
    );
  };

  render() {
    const { isResponsive = false } = this.props;

    return (
      <Container sizeContainer={this.props.sizeContainer}>
        <ContainerForm isResponsive={isResponsive}>
          <Label fsize={'20px'} defaultSign={this.props.defaultSign}>
            {this.props.status
              ? 'Regístrate en Homie'
              : 'Regístrate para publicar tu inmueble'}
          </Label>

          <Formik
            initialValues={this.defaultValues()}
            validationSchema={this.validationSchema()}
            onSubmit={this.submitData}
            render={this.SignUpForm}
          />
        </ContainerForm>
      </Container>
    );
  }
}

export default withRouter(View);
View.contextType = HomieContext;
